import {useEffect, useMemo, useState, type ReactNode} from "react";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {
  addAccountAddress,
  addAccountCompany,
  changeAccountPassword,
  createInfluencerVoucher,
  deleteAccountAddress,
  deleteAccountCompany,
  deleteNetworkMember,
  generateNetworkVoucher,
  getAccountAddresses,
  getAccountCompanies,
  getAccountCounties,
  getAccountInfluencer,
  getAccountNetwork,
  getAccountNotifications,
  getAccountOrders,
  getAccountProfile,
  getAccountReturns,
  refreshNetworkCode,
  requestInfluencerPayout,
  updateAccountAddress,
  updateAccountCompany,
  updateAccountProfile,
  cancelStockNotification,
  type AccountNetwork,
  type AccountOrder,
  type AccountProfile,
  type AccountReturn,
  type County,
  type FieldRule,
  type InfluencerDashboard,
  type InfluencerScope,
  type Product,
  type SavedAddress,
  type SavedCompany,
  checkoutVoucherShareUrl
} from "@zaaaz/shared";
import {useFormValidation} from "@/lib/useFormValidation";
import {ProductCard} from "./ProductCard";
import {ShopSkeleton} from "./ShopSkeleton";
import {DsButton, DsEmptyState, DsInput} from "@zaaaz/ui";
import {useI18n} from "./I18nProvider";
import {MoneyDisplay} from "./MoneyDisplay";
import {FormField} from "./FormField";
import {AccountModal} from "./AccountModal";
import {PasswordField} from "./PasswordField";
import {AccountChrome, accountLogout} from "./AccountChrome";
import {paths, type AccountTabId} from "@/lib/paths";

export type {AccountTabId};
type Tab = AccountTabId;

type ProfileField = "nume" | "telefon" | "email";
type PassField = "parola1" | "parola2";
type AddressField = "nume" | "adresa" | "judet" | "localitate";
type CompanyField = "firma_nume" | "firma_cui" | "firma_j" | "firma_sediu" | "firma_banca" | "firma_cont";

const profileRules: FieldRule<ProfileField>[] = [
  {field: "nume", required: true},
  {field: "telefon", required: true},
  {field: "email", type: "email", required: true}
];
const passRules: FieldRule<PassField>[] = [
  {field: "parola1", type: "password", required: true},
  {field: "parola2", type: "password", required: true}
];
const addressRules: FieldRule<AddressField>[] = [
  {field: "nume", required: true},
  {field: "adresa", required: true},
  {field: "judet", type: "select", required: true},
  {field: "localitate", required: true}
];
const companyRules: FieldRule<CompanyField>[] = [
  {field: "firma_nume", required: true},
  {field: "firma_cui", required: true},
  {field: "firma_j", required: true},
  {field: "firma_sediu", required: true},
  {field: "firma_banca", required: true},
  {field: "firma_cont", required: true}
];

function AccountShellSkeleton() {
  return (
    <div className="cc-account cc-account--loading" aria-busy="true">
      <div className="cc-account__layout">
        <div className="cc-account__skSidebar">
          <div className="cc-account__skAvatar" />
          <div className="cc-account__skLine cc-account__skLine--short" />
          <div className="cc-account__skLine cc-account__skLine--xs" />
        </div>
        <div className="cc-account__skContent">
          <ShopSkeleton lines={5} />
        </div>
      </div>
    </div>
  );
}

function AccountTabSkeleton() {
  return (
    <div className="cc-account__panel cc-account__panel--enter" aria-busy="true">
      <div className="cc-account__skCards">
        {Array.from({length: 3}).map((_, i) => <div key={i} className="cc-account__skCard" />)}
      </div>
    </div>
  );
}

function AccountPanel({tab, children}: {tab: Tab; children: ReactNode}) {
  return (
    <div key={tab || "orders"} className="cc-account__panel cc-account__panel--enter">
      {children}
    </div>
  );
}

function AccordionCard({open, onToggle, head, children, index = 0}: {open: boolean; onToggle: () => void; head: ReactNode; children: ReactNode; index?: number}) {
  return (
    <div className={`cc-account__card cc-account__accordion${open ? " isOpen" : ""}`} style={{"--acc-i": index} as React.CSSProperties}>
      <button type="button" className="cc-account__cardHead" onClick={onToggle} aria-expanded={open}>
        <span className="cc-account__cardHeadMain">{head}</span>
        <i className="fa fa-chevron-down cc-account__chevron" aria-hidden="true" />
      </button>
      <div className="cc-account__accordionBody">
        <div className="cc-account__accordionInner">
          <div className="cc-account__cardBody">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function AccountShell({tab}: {tab: Tab}) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getAccountProfile().then(setProfile).catch((e: Error) => setError(e.message));
  }, []);
  const active = tab || "";
  if(error) return <div className="cc-account"><p className="cc-account__alert">{error}</p></div>;
  if(!profile) return <AccountShellSkeleton />;
  return (
    <AccountChrome
      profile={profile}
      activeTab={active}
      onLogout={(e) => { e.preventDefault(); accountLogout(navigate); }}
    >
      <AccountPanel tab={active}>
        {active === "" ? <OrdersTab /> : null}
        {active === "retur" ? <ReturnsTab /> : null}
        {active === "informatii" ? <ProfileTab profile={profile} onUpdate={setProfile} /> : null}
        {active === "retea" ? <NetworkTab /> : null}
        {active === "influencer" ? (
          profile.influencer ? <InfluencerTab /> : <Navigate to={paths.account} replace />
        ) : null}
        {active === "adrese" ? <AddressesTab /> : null}
        {active === "firme" ? <CompaniesTab /> : null}
        {active === "notificari" ? <NotificationsTab /> : null}
      </AccountPanel>
    </AccountChrome>
  );
}

function OrdersTab() {
  const {t} = useI18n();
  const [items, setItems] = useState<AccountOrder[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getAccountOrders()
      .then((r) => { setItems(r.items); })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  if(loading) return <AccountTabSkeleton />;
  if(!items.length) return <DsEmptyState title={t("You have not placed any orders on our site yet.")} />;
  return (
    <div className="cc-account__orderList">
      {items.map((o, i) => (
        <Link key={o.id} to={paths.accountOrder(o.id)} className="cc-account__orderRow anim" style={{"--acc-i": i} as React.CSSProperties}>
          <div className="cc-account__orderRowBody">
            <div className="cc-account__orderRowTop">
              <span className="cc-account__orderRowId">{t("Order")} #{o.displayNumber}</span>
              <span className={`cc-account__badge cc-account__badge--sts${o.status}`}>{o.statusLabel}</span>
            </div>
            <div className="cc-account__orderRowMeta">
              <span><i className="fa fa-calendar" /> {o.date}</span>
              <span><i className="fa fa-money" /> <MoneyDisplay lei={o.total} eurRate={o.eurRate} /></span>
              <span className="cc-account__orderRowHint">{t("View order details")}</span>
            </div>
          </div>
          <i className="fa fa-angle-right cc-account__orderRowArrow" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

function ReturnsTab() {
  const {t} = useI18n();
  const [items, setItems] = useState<AccountReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    setLoading(true);
    getAccountReturns()
      .then((r) => { setItems(r.items); })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  if(loading) return <AccountTabSkeleton />;
  if(!items.length) return <DsEmptyState title={t("You have not placed any return requests on our site yet.")} />;
  return (
    <>
      {items.map((r, i) => (
        <AccordionCard key={r.id} index={i} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} head={
          <>
            {t("Return")} #{r.id}
            <span className={`cc-account__badge cc-account__badge--sts${r.status}`}>{r.statusLabel}</span>
            <span className="cc-account__cardMeta"><i className="fa fa-calendar" /> {r.date}</span>
          </>
        }>
          <h2 className="cc-account__sectionTitle">{t("Return information")}</h2>
          <div className="cc-account__line"><b>{t("Order date")}:</b> {r.orderDate}</div>
          <div className="cc-account__line"><b>{t("Products")}:</b> {r.products}</div>
          <div className="cc-account__line"><b>{t("Return motive")}:</b> {r.motive}</div>
          {r.bank ? <div className="cc-account__line"><b>{t("Bank details")}:</b> {r.bank} - {r.iban}</div> : null}
          <div className="cc-account__line"><b>{t("The desired solution")}:</b> {r.option}</div>
        </AccordionCard>
      ))}
    </>
  );
}

function ProfileTab({profile, onUpdate}: {profile: AccountProfile; onUpdate: (p: AccountProfile) => void}) {
  const {t} = useI18n();
  const [form, setForm] = useState({nume: profile.nume, email: profile.email, telefon: profile.telefon});
  const [pass, setPass] = useState({parola1: "", parola2: ""});
  const [showPass2, setShowPass2] = useState(false);
  const [msg, setMsg] = useState("");
  const [passMsg, setPassMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const {errors, clearError, validate} = useFormValidation<ProfileField>();
  const {errors: passErrors, clearError: clearPassError, validate: validatePass} = useFormValidation<PassField>();
  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setMsg("");
    if(!validate(profileRules, form, t)) return;
    setSaving(true);
    try {
      await updateAccountProfile(form);
      onUpdate({...profile, ...form});
      setMsg(t("Saved!"));
    } finally {
      setSaving(false);
    }
  }
  async function savePass(e: React.FormEvent) {
    e.preventDefault();
    setPassMsg("");
    if(!validatePass(passRules, pass, t)) return;
    setSavingPass(true);
    try {
      await changeAccountPassword(pass);
      setPass({parola1: "", parola2: ""});
      setPassMsg(t("Saved!"));
    } catch {
      setPassMsg(t("The two passwords are different."));
    } finally {
      setSavingPass(false);
    }
  }
  return (
    <div className="cc-account__profileStack">
      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-user" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Personal information")}</h2>
            <p className="cc-account__sectionCardSub">{t("Name and surname")}, {t("Phone number")}, E-mail</p>
          </div>
        </header>
        <form className="cc-account__sectionCardBody" onSubmit={saveProfile} noValidate>
          <DsInput type="text" name="nume" value={form.nume} onChange={(e) => { setForm({...form, nume: e.target.value}); clearError("nume"); }} placeholder={t("Name and surname")} className="cc-account__input" error={errors.nume} aria-required="true" />
          <div className="cc-account__fieldRow">
            <DsInput type="text" name="telefon" value={form.telefon} onChange={(e) => { setForm({...form, telefon: e.target.value}); clearError("telefon"); }} placeholder={t("Phone number")} className="cc-account__input" error={errors.telefon} aria-required="true" />
            <DsInput type="email" name="email" value={form.email} onChange={(e) => { setForm({...form, email: e.target.value}); clearError("email"); }} placeholder="E-mail" className="cc-account__input" error={errors.email} aria-required="true" />
          </div>
          {msg ? <div className="cc-account__msg cc-account__msg--ok cc-account__msg--pop">{msg}</div> : null}
          <div className="cc-account__sectionCardActions">
            <DsButton type="submit" variant="primary" size="md" disabled={saving}>{saving ? t("Saving...") : t("Save")}</DsButton>
          </div>
        </form>
      </section>

      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon cc-account__sectionCardIcon--gold" aria-hidden="true"><i className="fa fa-lock" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Password & security")}</h2>
            <p className="cc-account__sectionCardSub">{t("Change account password.")}</p>
          </div>
        </header>
        <form className="cc-account__sectionCardBody" onSubmit={savePass} noValidate>
          <FormField error={passErrors.parola1} className="cc-account__field">
            <PasswordField
              name="parola1"
              value={pass.parola1}
              onChange={(v) => { setPass({...pass, parola1: v}); clearPassError("parola1"); }}
              placeholder={t("New password")}
              showStrength
            />
          </FormField>
          <FormField error={passErrors.parola2} className="cc-account__field">
            <div className="cc-account__passWrap">
              <div className="cc-account__passInputRow">
                <input
                  type={showPass2 ? "text" : "password"}
                  name="parola2"
                  value={pass.parola2}
                  onChange={(e) => { setPass({...pass, parola2: e.target.value}); clearPassError("parola2"); }}
                  placeholder={t("Retype the new password")}
                  className="cc-account__input"
                  autoComplete="new-password"
                  aria-required="true"
                />
                <button type="button" className="cc-account__passToggle anim" onClick={() => setShowPass2(!showPass2)} aria-label={showPass2 ? t("Hide password") : t("Show password")}>
                  <i className={`fa ${showPass2 ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
                </button>
              </div>
            </div>
          </FormField>
          {passMsg ? <div className={`cc-account__msg cc-account__msg--pop${passMsg.includes("different") ? " cc-account__msg--err" : " cc-account__msg--ok"}`}>{passMsg}</div> : null}
          <div className="cc-account__sectionCardActions">
            <button type="submit" className={`cc-account__btn anim${savingPass ? " isLoading" : ""}`} disabled={savingPass}>{savingPass ? t("Saving...") : t("Change")}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function NetworkTab() {
  const {t} = useI18n();
  const [net, setNet] = useState<AccountNetwork | null>(null);
  const [amount, setAmount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [voucherLoading, setVoucherLoading] = useState(false);
  useEffect(() => { getAccountNetwork().then((r) => { setNet(r); setAmount(r.claimable); }); }, []);
  if(!net) return <AccountTabSkeleton />;
  const network = net;
  async function onCopy(e: React.MouseEvent) {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(network.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }
  async function onRefresh(e: React.MouseEvent) {
    e.preventDefault();
    setRefreshing(true);
    try {
      const r = await refreshNetworkCode();
      setNet((n) => n ? {...n, inviteCode: r.inviteCode} : n);
    } finally {
      setRefreshing(false);
    }
  }
  async function onVoucher(e: React.FormEvent) {
    e.preventDefault();
    setVoucherLoading(true);
    try {
      await generateNetworkVoucher(amount);
      const r = await getAccountNetwork();
      setNet(r);
      setAmount(r.claimable);
    } finally {
      setVoucherLoading(false);
    }
  }
  return (
    <div className="cc-account__network">
      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-users" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Your network")}</h2>
            <p className="cc-account__sectionCardSub">
              {t("Enjoy a")} {network.commissionPercent}% {t("commission on every order generated by your network")}
            </p>
          </div>
        </header>
        <div className="cc-account__sectionCardBody">
          <div className="cc-account__codeBox">
            <span className="cc-account__codeLabel">{t("Your access code for your network")}</span>
            <div className="cc-account__codeRow">
              <code>{network.inviteCode}</code>
              <button type="button" className={`cc-account__codeBtn anim${copied ? " isCopied" : ""}`} onClick={onCopy} title={t("Copy code")}>
                <i className={`fa ${copied ? "fa-check" : "fa-copy"}`} />
                <span>{copied ? t("Copied!") : t("Copy code")}</span>
              </button>
              <button type="button" className={`cc-account__codeRefresh anim${refreshing ? " isSpinning" : ""}`} onClick={onRefresh} title={t("Change")}>
                <i className="fa fa-refresh" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="cc-account__stats">
        <div className="cc-account__stat" style={{"--stat-i": 0} as React.CSSProperties}><b>{t("Network size")}</b><span>{network.networkSize}</span></div>
        <div className="cc-account__stat" style={{"--stat-i": 1} as React.CSSProperties}><b>{t("Network total orders")}</b><span>{network.networkOrders}</span></div>
        <div className="cc-account__stat" style={{"--stat-i": 2} as React.CSSProperties}><b>{t("Total revenue")}</b><span>{network.totalRevenue} Lei</span></div>
        <div className="cc-account__stat" style={{"--stat-i": 3} as React.CSSProperties}><b>{t("Total vouchers")}</b><span>{network.totalVouchers} Lei</span></div>
        <div className="cc-account__stat" style={{"--stat-i": 4} as React.CSSProperties}><b>{t("Unclaimed revenue")}</b><span>{network.unclaimedRevenue} Lei</span></div>
      </div>

      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon cc-account__sectionCardIcon--gold" aria-hidden="true"><i className="fa fa-sitemap" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Your network")}</h2>
            <p className="cc-account__sectionCardSub">{network.members.length ? `${network.members.length}` : t("Unlock the full potential of your network by expanding your connections!")}</p>
          </div>
        </header>
        <div className="cc-account__sectionCardBody cc-account__sectionCardBody--flush">
          {network.members.length ? (
            <div className="cc-account__memberList">
              {network.members.map((m, i) => (
                <div key={m.id} className="cc-account__listItem cc-account__listItem--enter cc-account__listItem--flat" style={{"--acc-i": i} as React.CSSProperties}>
                  <div className="cc-account__memberMain">
                    <span className="cc-account__memberAvatar" aria-hidden="true">{m.nume.charAt(0).toUpperCase()}</span>
                    <div>
                      <span className="cc-account__memberName">{m.nume}</span>
                      <span className="cc-account__listMuted">{m.email}</span>
                    </div>
                  </div>
                  <div className="cc-account__listItemMeta">
                    <span><b>{t("Your comission")}</b> {m.commission} Lei</span>
                    <span><b>{t("Registration date")}</b> {m.registeredAt}</span>
                    <button type="button" className="cc-account__linkBtn" onClick={async () => { const r = await deleteNetworkMember(m.id); setNet(r); }}>{t("Delete member")}</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cc-account__emptyPad">
              <DsEmptyState title={t("Unlock the full potential of your network by expanding your connections!")} />
            </div>
          )}
        </div>
      </section>

      {network.claimable > 0 ? (
        <section className="cc-account__sectionCard">
          <header className="cc-account__sectionCardHead">
            <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-gift" /></span>
            <div>
              <h2 className="cc-account__sectionCardTitle">{t("Your benefits")}</h2>
              <p className="cc-account__sectionCardSub">{t("Generate voucher")}</p>
            </div>
          </header>
          <form className="cc-account__sectionCardBody" onSubmit={onVoucher}>
            <div className="cc-account__voucherRow">
              <div className="cc-account__field">
                <input type="number" step="0.01" min={1} max={network.claimable} className="cc-account__input" name="pct" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value))} />
              </div>
              <button type="submit" className={`cc-account__btn anim${voucherLoading ? " isLoading" : ""}`} disabled={voucherLoading}>
                {voucherLoading ? t("Saving...") : t("Generate voucher")}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {network.vouchers.length ? (
        <section className="cc-account__sectionCard">
          <header className="cc-account__sectionCardHead">
            <span className="cc-account__sectionCardIcon cc-account__sectionCardIcon--gold" aria-hidden="true"><i className="fa fa-ticket" /></span>
            <div>
              <h2 className="cc-account__sectionCardTitle">{t("Voucher code:")}</h2>
              <p className="cc-account__sectionCardSub">{network.vouchers.length}</p>
            </div>
          </header>
          <div className="cc-account__sectionCardBody cc-account__sectionCardBody--flush">
            {network.vouchers.map((v, i) => (
              <div key={v.cod} className={`cc-account__listItem cc-account__listItem--enter cc-account__listItem--flat${v.used ? " isUsed" : ""}`} style={{"--acc-i": i} as React.CSSProperties}>
                <span>{t("Voucher code:")} <b>{v.cod}</b></span>
                <div className="cc-account__listItemMeta">
                  <span><b>{t("Value")}</b> {v.valoare} Lei</span>
                  <span><b>{t("Valability")}</b> {v.used ? t("Used / Expired") : `${v.data1} - ${v.data2}`}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

type ScopeTreeNode = InfluencerScope & {children: ScopeTreeNode[]};

function scopeKey(type: string, id: number) {
  return `${type}:${id}`;
}

function buildInfluencerScopeTree(scopes: InfluencerScope[]): {categories: ScopeTreeNode[]; brands: ScopeTreeNode[]; orphans: ScopeTreeNode[]} {
  const cats = scopes.filter((s) => s.type === "category");
  const subs = scopes.filter((s) => s.type === "subcategory");
  const subsubs = scopes.filter((s) => s.type === "subsubcategory");
  const brands = scopes.filter((s) => s.type === "brand");
  const catIds = new Set(cats.map((c) => c.id));
  const subIds = new Set(subs.map((s) => s.id));

  const categories: ScopeTreeNode[] = cats.map((c) => ({
    ...c,
    children: subs
      .filter((s) => s.parentId === c.id)
      .map((sub) => ({
        ...sub,
        children: subsubs
          .filter((ss) => ss.parentId === sub.id)
          .map((ss) => ({...ss, children: [] as ScopeTreeNode[]}))
      }))
  }));

  const orphanSubs = subs
    .filter((s) => s.parentId == null || !catIds.has(s.parentId))
    .map((sub) => ({
      ...sub,
      children: subsubs
        .filter((ss) => ss.parentId === sub.id)
        .map((ss) => ({...ss, children: [] as ScopeTreeNode[]}))
    }));

  const orphanSubsubs = subsubs
    .filter((ss) => ss.parentId == null || !subIds.has(ss.parentId))
    .map((ss) => ({...ss, children: [] as ScopeTreeNode[]}));

  return {
    categories,
    brands: brands.map((b) => ({...b, children: [] as ScopeTreeNode[]})),
    orphans: [...orphanSubs, ...orphanSubsubs]
  };
}

function nodeMatchesQuery(node: ScopeTreeNode, q: string): boolean {
  if(!q) return true;
  if((node.label ?? "").toLowerCase().includes(q)) return true;
  return node.children.some((c) => nodeMatchesQuery(c, q));
}

function filterScopeTree(nodes: ScopeTreeNode[], q: string): ScopeTreeNode[] {
  if(!q) return nodes;
  return nodes
    .filter((n) => nodeMatchesQuery(n, q))
    .map((n) => ({...n, children: filterScopeTree(n.children, q)}));
}

function collectExpandKeys(nodes: ScopeTreeNode[], q: string, into: Set<string>) {
  for(const n of nodes) {
    if(n.children.length && (q ? nodeMatchesQuery(n, q) : false)) {
      into.add(scopeKey(n.type, n.id));
      collectExpandKeys(n.children, q, into);
    }
  }
}

function InfluencerScopeTreeRows({
  nodes,
  depth,
  expanded,
  selected,
  lockedPercent,
  defaultPercent,
  onToggleExpand,
  onToggleScope,
  typeLabel
}: {
  nodes: ScopeTreeNode[];
  depth: number;
  expanded: Set<string>;
  selected: Array<{type: string; id: number}>;
  lockedPercent: number | null;
  defaultPercent: number;
  onToggleExpand: (key: string) => void;
  onToggleScope: (type: string, id: number, percent: number) => void;
  typeLabel: (type: string) => string;
}) {
  return (
    <>
      {nodes.map((node) => {
        const key = scopeKey(node.type, node.id);
        const percent = node.percent ?? defaultPercent;
        const checked = selected.some((x) => x.type === node.type && x.id === node.id);
        const blocked = lockedPercent !== null && Math.abs(lockedPercent - percent) > 0.001 && !checked;
        const hasKids = node.children.length > 0;
        const isOpen = hasKids && expanded.has(key);
        return (
          <div key={key} className="cc-infScope__node" style={{"--inf-depth": depth} as React.CSSProperties}>
            <div className={`cc-infScope__row${checked ? " isSelected" : ""}${blocked ? " isBlocked" : ""}`}>
              {hasKids ? (
                <button
                  type="button"
                  className={`cc-infScope__expand${isOpen ? " isOpen" : ""}`}
                  aria-expanded={isOpen}
                  aria-label={isOpen ? "Collapse" : "Expand"}
                  onClick={() => onToggleExpand(key)}
                >
                  <i className="fa fa-chevron-right" aria-hidden="true" />
                </button>
              ) : (
                <span className="cc-infScope__expandSpacer" aria-hidden="true" />
              )}
              <label className="cc-infScope__label">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={blocked}
                  onChange={() => onToggleScope(node.type, node.id, percent)}
                />
                <span className="cc-infScope__name">{node.label}</span>
                <span className="cc-infScope__meta">
                  <span className="cc-infScope__type">{typeLabel(node.type)}</span>
                  <span className="cc-infScope__pct">{percent}%</span>
                </span>
              </label>
            </div>
            {hasKids && isOpen ? (
              <div className="cc-infScope__children">
                <InfluencerScopeTreeRows
                  nodes={node.children}
                  depth={depth + 1}
                  expanded={expanded}
                  selected={selected}
                  lockedPercent={lockedPercent}
                  defaultPercent={defaultPercent}
                  onToggleExpand={onToggleExpand}
                  onToggleScope={onToggleScope}
                  typeLabel={typeLabel}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function daysRemainingUntil(dateStr: string): number | null {
  const raw = (dateStr || "").trim();
  if(!raw) return null;
  const end = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T23:59:59` : raw);
  if(Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
}

function InfluencerVoucherRow({
  voucher,
  index,
  scopeCatalog
}: {
  voucher: InfluencerDashboard["vouchers"][number];
  index: number;
  scopeCatalog: InfluencerScope[];
}) {
  const {t} = useI18n();
  const [copied, setCopied] = useState(false);
  const remaining = daysRemainingUntil(voucher.data2);
  const scopeLabels = (voucher.scopes ?? []).map((s) => {
    const meta = scopeCatalog.find((x) => x.type === s.type && x.id === s.id);
    return meta?.label?.trim() || `${s.type} #${s.id}`;
  });

  async function onCopy() {
    try {
      const share = checkoutVoucherShareUrl(window.location.origin, paths.checkout, voucher.cod);
      await navigator.clipboard.writeText(share);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  }

  return (
    <article
      className="cc-infVoucher cc-account__listItem--enter"
      style={{"--acc-i": index} as React.CSSProperties}
    >
      <div className="cc-infVoucher__top">
        <div className="cc-infVoucher__main">
          <div className="cc-infVoucher__ticket" aria-hidden="true">
            <i className="fa fa-ticket" />
          </div>
          <div className="cc-infVoucher__codeBlock">
            <span className="cc-infVoucher__label">{t("Voucher code")}</span>
            <div className="cc-infVoucher__codeRow">
              <code className="cc-infVoucher__code">{voucher.cod}</code>
              <button
                type="button"
                className={`cc-infVoucher__copy${copied ? " isCopied" : ""}`}
                onClick={onCopy}
                title={t("Copy checkout link")}
              >
                <i className={`fa ${copied ? "fa-check" : "fa-clone"}`} aria-hidden="true" />
                <span>{copied ? t("Link copied") : t("Copy checkout link")}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="cc-infVoucher__meta">
          <div className="cc-infVoucher__cell">
            <span className="cc-infVoucher__label">{t("Commission")}</span>
            <span className="cc-infVoucher__pct">{voucher.percent}%</span>
          </div>
          <div className="cc-infVoucher__cell cc-infVoucher__cell--dates">
            <span className="cc-infVoucher__label">{t("Validity")}</span>
            <span className="cc-infVoucher__dates">
              <span className="cc-infVoucher__datePart">
                <span className="cc-infVoucher__datePrefix">{t("from")}</span> {voucher.data1}
              </span>
              <span className="cc-infVoucher__datesSep" aria-hidden="true">→</span>
              <span className="cc-infVoucher__datePart">
                <span className="cc-infVoucher__datePrefix">{t("to")}</span> {voucher.data2}
              </span>
            </span>
            {remaining !== null ? (
              <span className={`cc-infVoucher__remain${remaining === 0 ? " isExpired" : remaining <= 3 ? " isSoon" : ""}`}>
                {remaining === 0
                  ? t("Expired")
                  : remaining === 1
                    ? t("1 day remaining")
                    : `${remaining} ${t("days remaining")}`}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {scopeLabels.length ? (
        <div className="cc-infVoucher__applies">
          <span className="cc-infVoucher__appliesLabel">{t("Applies to")}</span>
          <div className="cc-infVoucher__appliesLine" title={scopeLabels.join(", ")}>
            {scopeLabels.map((label, i) => (
              <span key={`${label}-${i}`} className="cc-infVoucher__appliesItem">
                {i > 0 ? <span className="cc-infVoucher__appliesDot" aria-hidden="true">·</span> : null}
                <span>{label}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function InfluencerTab() {
  const {t} = useI18n();
  const [data, setData] = useState<InfluencerDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [validDays, setValidDays] = useState(7);
  const [selected, setSelected] = useState<Array<{type: string; id: number}>>([]);
  const [lockedPercent, setLockedPercent] = useState<number | null>(null);
  const [paymentDetails, setPaymentDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [brandQuery, setBrandQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  function load() {
    setLoading(true);
    getAccountInfluencer()
      .then((r) => { setData(r); setLoading(false); setError(""); })
      .catch((e: Error) => { setError(e.message); setLoading(false); });
  }
  useEffect(() => { load(); }, []);

  const tree = useMemo(() => {
    if(!data) return {categories: [] as ScopeTreeNode[], brands: [] as ScopeTreeNode[], orphans: [] as ScopeTreeNode[]};
    const eligible = data.scopes.filter(
      (s) => lockedPercent === null || Math.abs((s.percent ?? data.defaultPercent) - lockedPercent) < 0.001
    );
    return buildInfluencerScopeTree(eligible);
  }, [data, lockedPercent]);

  const cq = catQuery.trim().toLowerCase();
  const bq = brandQuery.trim().toLowerCase();
  const filteredCats = useMemo(() => filterScopeTree(tree.categories, cq), [tree.categories, cq]);
  const filteredBrands = useMemo(() => filterScopeTree(tree.brands, bq), [tree.brands, bq]);
  const filteredOrphans = useMemo(() => filterScopeTree(tree.orphans, cq), [tree.orphans, cq]);

  useEffect(() => {
    if(!cq) return;
    const next = new Set<string>();
    collectExpandKeys(filteredCats, cq, next);
    collectExpandKeys(filteredOrphans, cq, next);
    setExpanded(next);
  }, [cq, filteredCats, filteredOrphans]);

  const selectedLabels = useMemo(() => {
    if(!data) return {brands: [] as string[], categories: [] as string[]};
    const brands: string[] = [];
    const categories: string[] = [];
    for(const s of selected) {
      const meta = data.scopes.find((x) => x.type === s.type && x.id === s.id);
      const label = meta?.label?.trim() || `${s.type} #${s.id}`;
      if(s.type === "brand") brands.push(label);
      else categories.push(label);
    }
    return {brands, categories};
  }, [data, selected]);
  function toggleScope(type: string, id: number, percent: number) {
    const key = scopeKey(type, id);
    const exists = selected.some((s) => scopeKey(s.type, s.id) === key);
    if(exists) {
      const next = selected.filter((s) => scopeKey(s.type, s.id) !== key);
      setSelected(next);
      if(!next.length) setLockedPercent(null);
      return;
    }
    if(lockedPercent !== null && Math.abs(lockedPercent - percent) > 0.001) return;
    setSelected([...selected, {type, id}]);
    setLockedPercent(percent);
  }

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if(next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function typeLabel(type: string) {
    if(type === "category") return t("Category");
    if(type === "subcategory") return t("Subcategory");
    if(type === "subsubcategory") return t("Sub-subcategory");
    if(type === "brand") return t("Brand");
    return type;
  }

  async function onGenerate(e: React.FormEvent) {
    e.preventDefault();
    if(!selected.length) return;
    setBusy(true);
    setError("");
    try {
      const r = await createInfluencerVoucher({validDays, scopes: selected});
      setData(r);
      setSelected([]);
      setLockedPercent(null);
    } catch(err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function onPayout(e: React.FormEvent) {
    e.preventDefault();
    if(!paymentDetails.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await requestInfluencerPayout(paymentDetails.trim());
      setData(r);
      setPaymentDetails("");
    } catch(err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  if(loading) return <AccountTabSkeleton />;
  if(error && !data) return <p className="cc-account__alert">{error}</p>;
  if(!data) return null;
  if(!data.enabled) {
    return <p className="cc-account__alert">{t("Influencer access is not enabled for this account.")}</p>;
  }

  const hasCatResults = filteredCats.length + filteredOrphans.length > 0;
  const hasBrandResults = filteredBrands.length > 0;

  return (
    <div className="cc-account__stack cc-inf">
      {error ? <p className="cc-account__alert">{error}</p> : null}

      <section className="cc-account__sectionCard cc-inf__hero">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon cc-account__sectionCardIcon--gold" aria-hidden="true"><i className="fa fa-star" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Influencer dashboard")}</h2>
            <p className="cc-account__sectionCardSub">{t("Create codes, track commissions, and request payouts.")}</p>
          </div>
        </header>
        <div className="cc-account__sectionCardBody">
          <div className="cc-account__stats cc-inf__stats">
            <div className="cc-account__stat" style={{"--stat-i": 0} as React.CSSProperties}><b>{t("Total generated")}</b><span>{data.totals.generated} Lei</span></div>
            <div className="cc-account__stat" style={{"--stat-i": 1} as React.CSSProperties}><b>{t("Available for payout")}</b><span>{data.totals.available} Lei</span></div>
            <div className="cc-account__stat" style={{"--stat-i": 2} as React.CSSProperties}><b>{t("Pending payout")}</b><span>{data.totals.pending} Lei</span></div>
            <div className="cc-account__stat" style={{"--stat-i": 3} as React.CSSProperties}><b>{t("Paid out")}</b><span>{data.totals.paid} Lei</span></div>
          </div>
        </div>
      </section>

      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-ticket" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Generate influencer voucher")}</h2>
            <p className="cc-account__sectionCardSub">{t("Pick categories or brands with the same commission percent.")}</p>
          </div>
        </header>
        <form className="cc-account__sectionCardBody" onSubmit={onGenerate}>
          <div className="cc-inf__genTop">
            <div className="cc-account__field cc-inf__days">
              <label htmlFor="inf-valid-days">{t("Validity (days)")}</label>
              <select id="inf-valid-days" className="cc-account__input" value={validDays} onChange={(e) => setValidDays(parseInt(e.target.value, 10))}>
                <option value={7}>7</option>
                <option value={15}>15</option>
                <option value={30}>30</option>
              </select>
            </div>
          </div>
          {lockedPercent !== null ? (
            <p className="cc-account__hint">{t("You can only combine scopes with the same percent. For a different percent, generate a separate voucher.")}</p>
          ) : null}

          <div className="cc-infScopeCols" role="group" aria-label={t("Select scopes")}>
            <div className="cc-infScopeCol">
              <div className="cc-infScopeCol__head">
                <h3 className="cc-infScopeCol__title">{t("Categories")}</h3>
                <p className="cc-infScopeCol__sub">{t("Expand to pick subcategories")}</p>
              </div>
              <div className="cc-infScope__search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="cc-account__input"
                  placeholder={t("Search categories...")}
                  value={catQuery}
                  onChange={(e) => setCatQuery(e.target.value)}
                  aria-label={t("Search categories...")}
                />
              </div>
              <div className="cc-infScope__panel cc-infScope__panel--col">
                {!hasCatResults ? (
                  <p className="cc-infScope__empty">{t("No matching scopes")}</p>
                ) : (
                  <>
                    {filteredCats.length ? (
                      <InfluencerScopeTreeRows
                        nodes={filteredCats}
                        depth={0}
                        expanded={expanded}
                        selected={selected}
                        lockedPercent={lockedPercent}
                        defaultPercent={data.defaultPercent}
                        onToggleExpand={toggleExpand}
                        onToggleScope={toggleScope}
                        typeLabel={typeLabel}
                      />
                    ) : null}
                    {filteredOrphans.length ? (
                      <div className="cc-infScope__group">
                        <h4 className="cc-infScope__groupTitle">{t("Other")}</h4>
                        <InfluencerScopeTreeRows
                          nodes={filteredOrphans}
                          depth={0}
                          expanded={expanded}
                          selected={selected}
                          lockedPercent={lockedPercent}
                          defaultPercent={data.defaultPercent}
                          onToggleExpand={toggleExpand}
                          onToggleScope={toggleScope}
                          typeLabel={typeLabel}
                        />
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <div className="cc-infScopeCol">
              <div className="cc-infScopeCol__head">
                <h3 className="cc-infScopeCol__title">{t("Brands")}</h3>
                <p className="cc-infScopeCol__sub">{t("Browse or search brands")}</p>
              </div>
              <div className="cc-infScope__search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="cc-account__input"
                  placeholder={t("Search brands...")}
                  value={brandQuery}
                  onChange={(e) => setBrandQuery(e.target.value)}
                  aria-label={t("Search brands...")}
                />
              </div>
              <div className="cc-infScope__panel cc-infScope__panel--col">
                {!hasBrandResults ? (
                  <p className="cc-infScope__empty">{t("No matching scopes")}</p>
                ) : (
                  <InfluencerScopeTreeRows
                    nodes={filteredBrands}
                    depth={0}
                    expanded={expanded}
                    selected={selected}
                    lockedPercent={lockedPercent}
                    defaultPercent={data.defaultPercent}
                    onToggleExpand={toggleExpand}
                    onToggleScope={toggleScope}
                    typeLabel={typeLabel}
                  />
                )}
              </div>
            </div>
          </div>

          {selected.length ? (
            <div className="cc-infSummary" role="status">
              <div className="cc-infSummary__icon" aria-hidden="true"><i className="fa fa-check-circle" /></div>
              <div className="cc-infSummary__body">
                <p className="cc-infSummary__lead">{t("Selection summary")}</p>
                <p className="cc-infSummary__text">
                  {t("You chose")}{" "}
                  {selectedLabels.brands.length ? (
                    <>
                      <strong>{selectedLabels.brands.length === 1 ? t("brand") : t("brands")}</strong>
                      {": "}
                      <span className="cc-infSummary__names">{selectedLabels.brands.join(", ")}</span>
                    </>
                  ) : (
                    <span className="cc-infSummary__muted">{t("no brands")}</span>
                  )}
                  {" · "}
                  {selectedLabels.categories.length ? (
                    <>
                      <strong>{selectedLabels.categories.length === 1 ? t("category") : t("categories")}</strong>
                      {": "}
                      <span className="cc-infSummary__names">{selectedLabels.categories.join(", ")}</span>
                    </>
                  ) : (
                    <span className="cc-infSummary__muted">{t("no categories")}</span>
                  )}
                  {" — "}
                  {t("offering")}{" "}
                  <span className="cc-infSummary__pct">{lockedPercent ?? data.defaultPercent}%</span>{" "}
                  {t("discount")}
                </p>
                <div className="cc-infSummary__chips">
                  {selected.map((s) => {
                    const meta = data.scopes.find((x) => x.type === s.type && x.id === s.id);
                    const name = meta?.label?.trim() || `${s.type} #${s.id}`;
                    const isBrand = s.type === "brand";
                    return (
                      <span
                        key={`${s.type}-${s.id}`}
                        className={`cc-infSummary__chip${isBrand ? " cc-infSummary__chip--brand" : " cc-infSummary__chip--cat"}`}
                      >
                        {name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <p className="cc-account__hint">{t("Select categories and/or brands below, then generate your voucher.")}</p>
          )}

          <div className="cc-account__sectionCardActions">
            <button type="submit" className={`cc-account__btn anim${busy ? " isLoading" : ""}`} disabled={busy || !selected.length}>
              {busy ? t("Saving...") : t("Generate voucher")}
            </button>
          </div>
        </form>
      </section>

      {data.totals.available > 0 ? (
        <section className="cc-account__sectionCard">
          <header className="cc-account__sectionCardHead">
            <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-university" /></span>
            <div>
              <h2 className="cc-account__sectionCardTitle">{t("Request payout")}</h2>
              <p className="cc-account__sectionCardSub">{t("Available for payout")}: {data.totals.available} Lei</p>
            </div>
          </header>
          <form className="cc-account__sectionCardBody" onSubmit={onPayout}>
            <div className="cc-account__field">
              <label htmlFor="inf-payout">{t("Payment details (IBAN, etc.)")}</label>
              <textarea
                id="inf-payout"
                className="cc-account__input"
                rows={3}
                placeholder={t("Payment details (IBAN, etc.)")}
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                required
              />
            </div>
            <div className="cc-account__sectionCardActions">
              <button type="submit" className={`cc-account__btn anim${busy ? " isLoading" : ""}`} disabled={busy}>
                {busy ? t("Saving...") : t("Request payout")}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-tags" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Your influencer vouchers")}</h2>
            <p className="cc-account__sectionCardSub">{t("Codes you generated for your audience.")}</p>
          </div>
        </header>
        <div className="cc-account__sectionCardBody cc-account__sectionCardBody--flush">
          {data.vouchers.length ? (
            <div className="cc-inf__list">
              {data.vouchers.map((v, i) => (
                <InfluencerVoucherRow key={v.id} voucher={v} index={i} scopeCatalog={data.scopes} />
              ))}
            </div>
          ) : (
            <div className="cc-account__emptyPad">
              <DsEmptyState title={t("No influencer vouchers yet")} />
            </div>
          )}
        </div>
      </section>

      <section className="cc-account__sectionCard">
        <header className="cc-account__sectionCardHead">
          <span className="cc-account__sectionCardIcon" aria-hidden="true"><i className="fa fa-line-chart" /></span>
          <div>
            <h2 className="cc-account__sectionCardTitle">{t("Commission orders")}</h2>
            <p className="cc-account__sectionCardSub">{t("Orders that earned you commission.")}</p>
          </div>
        </header>
        <div className="cc-account__sectionCardBody cc-account__sectionCardBody--flush">
          {data.earnings.length ? (
            <div className="cc-inf__list">
              {data.earnings.map((e, i) => (
                <div key={e.id} className="cc-account__listItem cc-account__listItem--enter cc-account__listItem--flat" style={{"--acc-i": i} as React.CSSProperties}>
                  <span>#{e.orderId} · {e.voucherCode}</span>
                  <div className="cc-account__listItemMeta">
                    <span><b>{e.commission} Lei</b> ({e.percent}% · base {e.baseAmount})</span>
                    <span>{e.available ? t("Available for payout") : t("Pending payout")}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cc-account__emptyPad">
              <DsEmptyState title={t("No influencer earnings yet")} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function countyLabel(counties: County[], judet: number) {
  return counties.find((c) => c.id === judet)?.nume ?? "";
}

const emptyAddress = {nume: "", adresa: "", judet: 0, localitate: "", codpostal: ""};

function AddressesTab() {
  const {t} = useI18n();
  const [items, setItems] = useState<SavedAddress[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{mode: "add"} | {mode: "edit"; id: number} | null>(null);
  const [form, setForm] = useState(emptyAddress);
  const [saving, setSaving] = useState(false);
  const {errors, clearError, validate, clearErrors} = useFormValidation<AddressField>();
  useEffect(() => {
    setLoading(true);
    Promise.all([getAccountAddresses(), getAccountCounties()]).then(([addrs, cnt]) => {
      setItems(addrs.items);
      setCounties(cnt.items);
      setLoading(false);
    });
  }, []);
  function openAdd() {
    setForm(emptyAddress);
    clearErrors();
    setModal({mode: "add"});
  }
  function openEdit(a: SavedAddress) {
    setForm({nume: a.nume, adresa: a.adresa, judet: a.judet, localitate: a.localitate, codpostal: String(a.codpostal || "")});
    clearErrors();
    setModal({mode: "edit", id: a.id});
  }
  function closeModal() {
    setModal(null);
    setForm(emptyAddress);
    clearErrors();
  }
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if(!validate(addressRules, form, t)) return;
    setSaving(true);
    try {
      if(modal?.mode === "edit") {
        const r = await updateAccountAddress(modal.id, {...form, codpostal: parseInt(String(form.codpostal), 10) || 0});
        setItems(r.items);
      } else {
        const r = await addAccountAddress({...form, codpostal: parseInt(String(form.codpostal), 10) || 0});
        setItems(r.items);
      }
      closeModal();
    } finally {
      setSaving(false);
    }
  }
  if(loading) return <AccountTabSkeleton />;
  return (
    <>
      <div className="cc-account__entityToolbar">
        <button type="button" className="cc-account__btn cc-account__btn--outline cc-account__btn--add anim" onClick={openAdd}>
          <i className="fa fa-plus" aria-hidden="true" /> {t("Add the new address")}
        </button>
      </div>
      {items.length ? (
        <div className="cc-account__entityGrid">
          {items.map((a, i) => (
            <article key={a.id} className="cc-account__entityCard anim" style={{"--acc-i": i} as React.CSSProperties}>
              <div className="cc-account__entityCardIcon" aria-hidden="true"><i className="fa fa-map-marker" /></div>
              <h3 className="cc-account__entityCardTitle">{a.nume}</h3>
              <p className="cc-account__entityCardLine">{a.adresa}</p>
              <p className="cc-account__entityCardLine cc-account__entityCardLine--muted">
                {a.localitate}{countyLabel(counties, a.judet) ? `, ${countyLabel(counties, a.judet)}` : ""}{a.codpostal ? ` · ${a.codpostal}` : ""}
              </p>
              <div className="cc-account__entityCardActions">
                <button type="button" className="cc-account__entityBtn anim" onClick={() => openEdit(a)}>{t("Edit address")}</button>
                <button type="button" className="cc-account__entityBtn cc-account__entityBtn--danger anim" onClick={async () => { const r = await deleteAccountAddress(a.id); setItems(r.items ?? []); }}>{t("Delete this address")}</button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <DsEmptyState title={t("No saved addresses yet")} />
      )}
      <AccountModal
        open={modal !== null}
        title={modal?.mode === "edit" ? t("Edit address") : t("Add the new address")}
        onClose={closeModal}
        footer={
          <div className="cc-accountModal__actions">
            <button type="button" className="cc-account__btn cc-account__btn--ghost anim" onClick={closeModal}>{t("Close")}</button>
            <button type="submit" form="addressModalForm" className={`cc-account__btn anim${saving ? " isLoading" : ""}`} disabled={saving}>{saving ? t("Saving...") : modal?.mode === "edit" ? t("Save modifications") : t("Add the new address")}</button>
          </div>
        }
      >
        <form id="addressModalForm" className="cc-account__form" onSubmit={onSubmit} noValidate>
          <FormField error={errors.nume} className="cc-account__field">
            <input type="text" value={form.nume} onChange={(e) => { setForm({...form, nume: e.target.value}); clearError("nume"); }} placeholder={t("Address name (for future use)")} className="cc-account__input" aria-required="true" />
          </FormField>
          <FormField error={errors.adresa} className="cc-account__field">
            <input type="text" value={form.adresa} onChange={(e) => { setForm({...form, adresa: e.target.value}); clearError("adresa"); }} placeholder={t("Address (street, number)")} className="cc-account__input" aria-required="true" />
          </FormField>
          <FormField error={errors.judet} className="cc-account__field">
            <select value={form.judet} onChange={(e) => { setForm({...form, judet: parseInt(e.target.value, 10)}); clearError("judet"); }} className="cc-account__select" aria-required="true">
              <option value={0}>{t("Choose county")}</option>
              {counties.map((j) => <option key={j.id} value={j.id}>{j.nume}</option>)}
            </select>
          </FormField>
          <div className="cc-account__fieldRow">
            <FormField error={errors.localitate} className="cc-account__field">
              <input type="text" value={form.localitate} onChange={(e) => { setForm({...form, localitate: e.target.value}); clearError("localitate"); }} placeholder={t("City")} className="cc-account__input" aria-required="true" />
            </FormField>
            <div className="cc-account__field">
              <input type="text" value={form.codpostal} onChange={(e) => setForm({...form, codpostal: e.target.value})} placeholder={t("Zip code")} className="cc-account__input" />
            </div>
          </div>
        </form>
      </AccountModal>
    </>
  );
}

const emptyCompany = {firma_nume: "", firma_cui: "", firma_j: "", firma_banca: "", firma_cont: "", firma_sediu: ""};

function CompaniesTab() {
  const {t} = useI18n();
  const [items, setItems] = useState<SavedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{mode: "add"} | {mode: "edit"; id: number} | null>(null);
  const [form, setForm] = useState(emptyCompany);
  const [saving, setSaving] = useState(false);
  const {errors, clearError, validate, clearErrors} = useFormValidation<CompanyField>();
  useEffect(() => {
    setLoading(true);
    getAccountCompanies()
      .then((r) => { setItems(r.items); })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  function openAdd() {
    setForm(emptyCompany);
    clearErrors();
    setModal({mode: "add"});
  }
  function openEdit(c: SavedCompany) {
    setForm({firma_nume: c.firma_nume, firma_cui: c.firma_cui, firma_j: c.firma_j, firma_banca: c.firma_banca, firma_cont: c.firma_cont, firma_sediu: c.firma_sediu});
    clearErrors();
    setModal({mode: "edit", id: c.id});
  }
  function closeModal() {
    setModal(null);
    setForm(emptyCompany);
    clearErrors();
  }
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if(!validate(companyRules, form, t)) return;
    setSaving(true);
    try {
      if(modal?.mode === "edit") {
        const r = await updateAccountCompany(modal.id, form);
        setItems(r.items);
      } else {
        const r = await addAccountCompany(form);
        setItems(r.items);
      }
      closeModal();
    } finally {
      setSaving(false);
    }
  }
  if(loading) return <AccountTabSkeleton />;
  return (
    <>
      <div className="cc-account__entityToolbar">
        <button type="button" className="cc-account__btn cc-account__btn--outline cc-account__btn--add anim" onClick={openAdd}>
          <i className="fa fa-plus" aria-hidden="true" /> {t("Add company")}
        </button>
      </div>
      {items.length ? (
        <div className="cc-account__entityGrid">
          {items.map((c, i) => (
            <article key={c.id} className="cc-account__entityCard anim" style={{"--acc-i": i} as React.CSSProperties}>
              <div className="cc-account__entityCardIcon cc-account__entityCardIcon--gold" aria-hidden="true"><i className="fa fa-building" /></div>
              <h3 className="cc-account__entityCardTitle">{c.firma_nume}</h3>
              <p className="cc-account__entityCardLine cc-account__entityCardLine--muted">CUI {c.firma_cui}</p>
              <p className="cc-account__entityCardLine">{c.firma_sediu}</p>
              <p className="cc-account__entityCardLine cc-account__entityCardLine--muted">{c.firma_banca} · {c.firma_cont}</p>
              <div className="cc-account__entityCardActions">
                <button type="button" className="cc-account__entityBtn anim" onClick={() => openEdit(c)}>{t("Edit company")}</button>
                <button type="button" className="cc-account__entityBtn cc-account__entityBtn--danger anim" onClick={async () => { const r = await deleteAccountCompany(c.id); setItems(r.items ?? []); }}>{t("Delete this company")}</button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <DsEmptyState title={t("No billing companies yet")} />
      )}
      <AccountModal
        open={modal !== null}
        title={modal?.mode === "edit" ? t("Edit company") : t("Add company")}
        onClose={closeModal}
        footer={
          <div className="cc-accountModal__actions">
            <button type="button" className="cc-account__btn cc-account__btn--ghost anim" onClick={closeModal}>{t("Close")}</button>
            <button type="submit" form="companyModalForm" className={`cc-account__btn anim${saving ? " isLoading" : ""}`} disabled={saving}>{saving ? t("Saving...") : modal?.mode === "edit" ? t("Save modifications") : t("Add company")}</button>
          </div>
        }
      >
        <form id="companyModalForm" className="cc-account__form" onSubmit={onSubmit} noValidate>
          <FormField error={errors.firma_nume} className="cc-account__field">
            <input type="text" value={form.firma_nume} onChange={(e) => { setForm({...form, firma_nume: e.target.value}); clearError("firma_nume"); }} placeholder={t("Company name")} className="cc-account__input" aria-required="true" />
          </FormField>
          <div className="cc-account__fieldRow">
            <FormField error={errors.firma_cui} className="cc-account__field">
              <input type="text" value={form.firma_cui} onChange={(e) => { setForm({...form, firma_cui: e.target.value}); clearError("firma_cui"); }} placeholder={t("VAT Number")} className="cc-account__input" aria-required="true" />
            </FormField>
            <FormField error={errors.firma_j} className="cc-account__field">
              <input type="text" value={form.firma_j} onChange={(e) => { setForm({...form, firma_j: e.target.value}); clearError("firma_j"); }} placeholder={t("Trade register no.")} className="cc-account__input" aria-required="true" />
            </FormField>
          </div>
          <FormField error={errors.firma_sediu} className="cc-account__field">
            <input type="text" value={form.firma_sediu} onChange={(e) => { setForm({...form, firma_sediu: e.target.value}); clearError("firma_sediu"); }} placeholder={t("Office address")} className="cc-account__input" aria-required="true" />
          </FormField>
          <div className="cc-account__fieldRow">
            <FormField error={errors.firma_banca} className="cc-account__field">
              <input type="text" value={form.firma_banca} onChange={(e) => { setForm({...form, firma_banca: e.target.value}); clearError("firma_banca"); }} placeholder={t("Bank")} className="cc-account__input" aria-required="true" />
            </FormField>
            <FormField error={errors.firma_cont} className="cc-account__field">
              <input type="text" value={form.firma_cont} onChange={(e) => { setForm({...form, firma_cont: e.target.value}); clearError("firma_cont"); }} placeholder={t("Bank account")} className="cc-account__input" aria-required="true" />
            </FormField>
          </div>
        </form>
      </AccountModal>
    </>
  );
}

function NotificationsTab() {
  const {t} = useI18n();
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getAccountNotifications()
      .then((r) => { setItems(r.items); })
      .catch(() => { setItems([]); })
      .finally(() => setLoading(false));
  }, []);
  async function onCancel(id: number) {
    await cancelStockNotification(id);
    setItems((prev) => prev.filter((p) => p.id !== id));
    window.dispatchEvent(new CustomEvent("notifications-updated"));
  }
  if(loading) return <AccountTabSkeleton />;
  if(!items.length) return <DsEmptyState title={t("You have not set any stock alerts.")} />;
  return (
    <div className="cc-account__notifGrid">
      <div className="cc-productGridWrap">
        <div className="cc-productGrid">
          {items.map((p, i) => (
            <div key={p.id} className="cc-account__notifItem cc-account__notifItem--enter" style={{"--acc-i": i} as React.CSSProperties}>
              <ProductCard product={p} />
              <a href="#" className="cc-account__notifCancel anim" onClick={(e) => { e.preventDefault(); onCancel(p.id); }}><i className="fa fa-times" /> {t("Cancel notification")}</a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
